import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

interface BlogPostData {
  title: string;
  date: string;
  readTime: string;
  content: JSX.Element;
}

const blogPosts: Record<string, BlogPostData> = {
  'how-to-use-intonation-when-presenting': {
    title: 'How to use intonation while presenting (TED talk Case Study)',
    date: 'March 15, 2024',
    readTime: '3 min watch',
    content: (
      <div className="space-y-8">
        <p className="text-lg text-presintention-muted leading-relaxed">
          Master the art of vocal variety to keep your audience engaged and make your presentations more impactful. Watch this expert TED talk case study.
        </p>
        
        <div className="flex justify-center">
          <div className="aspect-[9/16] w-full max-w-md rounded-lg overflow-hidden shadow-lg">
            <iframe
              src="https://www.tiktok.com/embed/7194084032978636037"
              className="w-full h-full"
              allowFullScreen
              scrolling="no"
              allow="encrypted-media"
            />
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 className="text-2xl font-bold text-presintention-dark mb-4">Key Takeaways</h2>
          <ul className="space-y-2 text-presintention-muted">
            <li>Vary your pitch to emphasize important points and maintain interest</li>
            <li>Use strategic pauses to let key messages sink in</li>
            <li>Match your tone to your content - serious topics need gravitas, inspiring messages need energy</li>
            <li>Study how TED speakers modulate their voice for maximum impact</li>
            <li>Record yourself to identify monotone patterns and areas for improvement</li>
          </ul>
        </div>
      </div>
    )
  },
  'how-to-prevent-nervous-shaking': {
    title: 'How to Prevent Nervous Shaking while presenting',
    date: 'March 12, 2024',
    readTime: '2 min watch',
    content: (
      <div className="space-y-8">
        <p className="text-lg text-presintention-muted leading-relaxed">
          Learn proven techniques to stay calm, composed, and in control during high-pressure presentations. Stop nervous shaking and present with confidence.
        </p>
        
        <div className="flex justify-center">
          <div className="aspect-[9/16] w-full max-w-md rounded-lg overflow-hidden shadow-lg">
            <iframe
              src="https://www.tiktok.com/embed/7200927149224660230"
              className="w-full h-full"
              allowFullScreen
              scrolling="no"
              allow="encrypted-media"
            />
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 className="text-2xl font-bold text-presintention-dark mb-4">Key Takeaways</h2>
          <ul className="space-y-2 text-presintention-muted">
            <li>Ground yourself physically before presenting - proper stance reduces shaking</li>
            <li>Use controlled breathing techniques to calm your nervous system</li>
            <li>Channel nervous energy into purposeful gestures</li>
            <li>Practice power poses before going on stage</li>
            <li>Focus on your message rather than your physical state</li>
          </ul>
        </div>
      </div>
    )
  },
  'neurochemistry-of-storytelling': {
    title: 'Neurochemistry of storytelling',
    date: 'March 10, 2024',
    readTime: '3 min watch',
    content: (
      <div className="space-y-8">
        <p className="text-lg text-presintention-muted leading-relaxed">
          Discover the fascinating science behind compelling narratives and how to use brain chemistry to captivate your audience. Learn what happens in your audience's brain when you tell a great story.
        </p>
        
        <div className="flex justify-center">
          <div className="aspect-[9/16] w-full max-w-md rounded-lg overflow-hidden shadow-lg">
            <iframe
              src="https://www.tiktok.com/embed/7300223047871614214"
              className="w-full h-full"
              allowFullScreen
              scrolling="no"
              allow="encrypted-media"
            />
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 className="text-2xl font-bold text-presintention-dark mb-4">Key Takeaways</h2>
          <ul className="space-y-2 text-presintention-muted">
            <li>Stories trigger dopamine release, making your message more memorable</li>
            <li>Emotional narratives activate oxytocin, building trust and connection</li>
            <li>The brain processes stories differently than facts - stories create neural coupling</li>
            <li>Use story structure to guide your audience through an emotional journey</li>
            <li>Combine data with narrative to engage both logical and emotional centers of the brain</li>
          </ul>
        </div>
      </div>
    )
  }
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  if (!slug || !blogPosts[slug]) {
    return <Navigate to="/blog" replace />;
  }

  const post = blogPosts[slug];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        <article className="py-20 bg-presintention-light section-padding">
          <div className="container-width max-w-4xl">
            <div className={`transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'}`}>
              <a 
                href="/blog" 
                className="inline-flex items-center text-presintention-accent hover:underline mb-8"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Blog
              </a>

              <div className="mb-8">
                <h1 className="text-4xl md:text-5xl font-bold mb-4 text-presintention-dark">
                  {post.title}
                </h1>
                <div className="flex items-center gap-3 text-presintention-muted">
                  <span>{post.date}</span>
                  <span>•</span>
                  <span>{post.readTime}</span>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
                {post.content}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
